import React from 'react';
import { useDispatch } from 'react-redux';
import usePurchase from 'hooks/store/usePurchase';
import { useTranslation } from 'react-i18next';
import { Spacing, Text, Box, Icon, Gap, Button } from '@reservamos/elements';
import moment from 'moment';
import PropTypes from 'prop-types';
import { togglePurchaseReviewOverlay } from '@/actions/overlayView';
import './BookingReview.scss';
import { bookingReviewDetailsClickedTracker } from 'metrics/user-analytics/purchase';
import { useWhitelabelFeatures } from '../../hooks/whitelabel/useWhitelabelFeatures';
import NextHoursRange from './NextHoursRange';
import passengerTypes from '../../config/passengerTypes';

/**
 * PassengerBox component.
 * Renders a box containing passenger information.
 * @param {object} props - children
 * @param {node} props.children - Content to be displayed inside the box
 */
const PassengerBox = ({ children }) => (
  <Box
    elementType="div"
    paddingVertical="S"
    paddingHorizontal="M"
    borderRadius="L"
    bgColor="accent"
    alphaBg="XS"
  >
    <Text weight="bold" size="S" textAlign="center">
      {children}
    </Text>
  </Box>
);

/**
 * BookingReview component.
 * Renders a booking review section with trip details including departure and destination information.
 * @param {object} props - departureDate, returnDate, passengers, isDepartureOpen, isReturnOpen, departureTerminal, departureCity, destinationTerminal, destinationCity, arrivalDate, duration, alwaysShow.
 * @param {string} props.departureDate - Departure date.
 * @param {string} props.returnDate - Return date.
 * @param {array} props.passengers - List of passengers.
 * @param {boolean} props.isDepartureOpen - Whether departure is open ticket.
 * @param {boolean} props.isReturnOpen - Whether return is open ticket.
 * @param {string} props.departureTerminal - Departure terminal name.
 * @param {string} props.departureCity - Departure city name.
 * @param {string} props.destinationTerminal - Destination terminal name.
 * @param {string} props.destinationCity - Destination city name.
 * @param {string} props.arrivalDate - Arrival datetime string for showing time info.
 * @param {number} props.duration - Trip duration in minutes.
 * @param {boolean} props.alwaysShow - Bypass BOOKING_REVIEW_ENABLED feature flag.
 */
const BookingReview = ({
  departureDate,
  returnDate,
  passengers,
  isDepartureOpen,
  isReturnOpen,
  departureTerminal,
  departureCity,
  destinationTerminal,
  destinationCity,
  arrivalDate,
  duration,
  alwaysShow,
  showTripDetailsButton,
  showPassengerCategories,
  showTitle,
}) => {
  const dispatch = useDispatch();
  const features = useWhitelabelFeatures();
  const purchase = usePurchase();
  const { bulkTicket: isBulkTicket } = purchase;
  const isFlat = features.FUNNEL_STYLE === 'FLAT';
  const { t } = useTranslation(['general', 'passengers']);
  const departureText = isDepartureOpen ? t('open_ticket') : moment(departureDate).format('DD MMM');
  const returnText = isReturnOpen ? t('open_ticket') : moment(returnDate).format('DD MMM');
  const showTimeInfo = Boolean(arrivalDate && duration && !isDepartureOpen);
  const departureTime = showTimeInfo ? moment(departureDate).format('h:mm A') : null;
  const arrivalTime = showTimeInfo ? moment(arrivalDate).format('h:mm A') : null;
  const durationHours = showTimeInfo ? Math.floor(duration / 60) : null;
  const durationMinutes = showTimeInfo ? duration % 60 : null;
  const durationText = showTimeInfo
    ? `${durationHours}${
        durationMinutes > 0 ? `:${String(durationMinutes).padStart(2, '0')}` : ''
      }hrs`
    : null;

  if (!features.BOOKING_REVIEW_ENABLED && !alwaysShow) return null;

  /**
   * Handles the click event for the trip details button.
   * Tracks the event and opens the purchase review overlay.
   */
  const handleOnClick = () => {
    bookingReviewDetailsClickedTracker(purchase);
    dispatch(togglePurchaseReviewOverlay(true));
  };

  /**
   * PassengerCategories component.
   * Renders a list of passengers or passenger categories based on ticket type.
   * @returns {JSX.Element} List of passengers or categories
   */
  const PassengerCategories = () => {
    if (!isBulkTicket) {
      const displayPassengers = passengers.slice(0, 2);
      const remainingPassengers = passengers.length - 2;

      return (
        <>
          {displayPassengers.map((passenger) => (
            <PassengerBox key={passenger.id}>
              {`${passenger.firstName} ${passenger.lastName}`}
            </PassengerBox>
          ))}
          {remainingPassengers > 0 && (
            <Text weight="bold" size="S" textAlign="center">
              {`+${remainingPassengers}`}
            </Text>
          )}
        </>
      );
    }

    const categoryCounts = passengers.reduce((acc, { busCategory }) => {
      const category = busCategory === 'special' ? 'pcd' : busCategory;
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {});

    const sortedCategories = Object.entries(categoryCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 2);
    const remainingCategories = Object.keys(categoryCounts).length - 2;

    return (
      <>
        {sortedCategories.map(([category, count]) => (
          <PassengerBox key={category}>
            {`${count} ${t(`passengers:${passengerTypes[category].label}`)}`}
          </PassengerBox>
        ))}
        {remainingCategories > 0 && (
          <Text weight="bold" size="S" textAlign="center">
            {`+${remainingCategories}`}
          </Text>
        )}
      </>
    );
  };

  return (
    <div>
      {showTitle && (
        <Text weight="bold" size="L">
          {t('trip_summary')}
        </Text>
      )}

      <div className="booking-review-wrapper">
        <div className="booking-review-hole-left">
          <div className="booking-review-hole-left-fill" />
        </div>
        <div className="booking-review-hole-right">
          <div className="booking-review-hole-right-fill" />
        </div>
        <div className="booking-review-container">
          <Spacing size="S" vertical>
            <Spacing isResponsive vertical justifyContent="space-between" size="S">
              <Spacing responsiveSize="S" justifyContent="space-between" isResponsive>
                <Spacing size="XS" alignItems="baseline">
                  <Icon type="Pin" size="S" />
                  <div>
                    <Text weight="bold">{`${destinationTerminal}, ${destinationCity}`}</Text>
                    <Text size="S">{`${t('from')} ${departureTerminal}, ${departureCity}`}</Text>
                  </div>
                </Spacing>
                {!showTimeInfo && (
                  <Spacing size="S">
                    <Gap columnGap="XS" alignItems="center">
                      <Icon size="S" type="CalendarOutlined" />
                      <Text size="S">{t(returnDate ? 'departure' : 'one_way_trip')}</Text>
                      <Text weight="bold" mobileSize="S" color="grayMedium">
                        {departureText}
                      </Text>
                    </Gap>
                    {returnDate && (
                      <Gap columnGap="XS" alignItems="center">
                        <Icon size="S" type="CalendarOutlined" />
                        <Text size="S">{t('return')}</Text>
                        <Text weight="bold" mobileSize="S" color="grayMedium">
                          {returnText}
                        </Text>
                      </Gap>
                    )}
                  </Spacing>
                )}
              </Spacing>

              {showTimeInfo && (
                <Spacing
                  responsiveSize="S"
                  justifyContent="space-between"
                  alignItems="center"
                  isResponsive
                >
                  <Spacing size="S" alignItems="center" isResponsive={false}>
                    <Text weight="bold" color="accent">
                      {departureText}
                    </Text>
                    <Spacing size="XS" alignItems="center" isResponsive={false}>
                      <Icon size="S" type="Bus" />
                      <Text size="S">{departureTime}</Text>
                      <Text size="S" color="grayMedium">
                        {durationText}
                      </Text>
                      <Text size="S">{arrivalTime}</Text>
                    </Spacing>
                  </Spacing>
                  <Text size="S">{t(returnDate ? 'departure' : 'one_way_trip')}</Text>
                </Spacing>
              )}

              <Spacing
                isResponsive
                responsiveColumnReverse
                justifyContent={isBulkTicket ? 'space-between' : 'center'}
              >
                {isBulkTicket && <NextHoursRange />}
                {showPassengerCategories && (
                  <Spacing alignItems="center" justifyContent="center">
                    <PassengerCategories />
                  </Spacing>
                )}
              </Spacing>
            </Spacing>
          </Spacing>

          {showTripDetailsButton && (
            <div className="booking-review-bottom">
              <Button
                color="accent"
                iconType="Info"
                iconColor="accent"
                text={t('trip_details')}
                variant="noFill"
                onClick={handleOnClick}
                isRounded={isFlat}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

BookingReview.propTypes = {
  departureDate: PropTypes.string.isRequired,
  returnDate: PropTypes.string.isRequired,
  passengers: PropTypes.array.isRequired,
  isDepartureOpen: PropTypes.bool,
  isReturnOpen: PropTypes.bool,
  departureTerminal: PropTypes.string,
  departureCity: PropTypes.string,
  destinationTerminal: PropTypes.string,
  destinationCity: PropTypes.string,
  arrivalDate: PropTypes.string,
  duration: PropTypes.number,
  alwaysShow: PropTypes.bool,
  showTripDetailsButton: PropTypes.bool,
  showPassengerCategories: PropTypes.bool,
  showTitle: PropTypes.bool,
};

PassengerBox.propTypes = {
  children: PropTypes.node.isRequired,
};

BookingReview.defaultProps = {
  isDepartureOpen: null,
  isReturnOpen: null,
  arrivalDate: null,
  duration: null,
  alwaysShow: false,
  showTripDetailsButton: true,
  showPassengerCategories: true,
  showTitle: true,
};

export default BookingReview;
